import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { TESTIMONIALS } from "../data/content";

const AUTO_MS = 6000;

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;
  const timer = useRef(null);

  const go = useCallback((next, direction) => {
    setDir(direction);
    setIndex((prev) => (next + count) % count);
  }, [count]);

  const next = useCallback(() => go(index + 1, 1), [go, index]);
  const prev = useCallback(() => go(index - 1, -1), [go, index]);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => go(index + 1, 1), AUTO_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused, go]);

  const t = TESTIMONIALS[index];
  const testId = `testimonial-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`;

  return (
    <section
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
      data-testid="testimonials-section"
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(55% 55% at 50% 30%, rgba(var(--glow),0.12), transparent 65%)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 mb-12 md:mb-16">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-primary-c/70" />
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c">Stories of Release</p>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.04]">From heaviness to deep rest.</h2>
        <p className="mt-4 text-fg/60 md:text-lg max-w-2xl">
          Educators, caregivers, families, and event leaders on the moment the weight lifted — and calm moved in.
        </p>
      </div>

      <div
        className="relative max-w-4xl mx-auto px-6 md:px-10"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        data-testid="testimonial-carousel"
      >
        <div className="relative min-h-[320px] md:min-h-[300px] flex items-center">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.figure
              key={index}
              custom={dir}
              initial={{ opacity: 0, x: dir * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -60 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="glass shape-r w-full p-10 md:p-14 text-center"
              data-testid={testId}
            >
              <Quote className="text-primary-c mx-auto mb-6" size={34} />
              <blockquote
                className="font-serif text-xl md:text-3xl leading-snug text-fg/90 max-w-3xl mx-auto"
                data-testid="testimonial-body"
              >
                “{t.body}”
              </blockquote>
              <figcaption className="mt-8 font-mono text-[11px] tracking-[0.25em] uppercase text-fg/60">
                {t.name} <span className="text-primary-c">· {t.role}</span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* controls */}
        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            onClick={prev}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev"
            className="grid place-items-center h-11 w-11 rounded-full border border-white/15 text-fg/70 hover:text-primary-c hover:border-primary-c/60 transition-colors"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="flex items-center gap-2.5" data-testid="testimonial-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => go(i, i > index ? 1 : -1)}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                data-active={i === index}
                className="h-2.5 rounded-full transition-all duration-400"
                style={{
                  width: i === index ? "26px" : "10px",
                  backgroundColor: i === index ? "var(--primary)" : "rgba(255,255,255,0.2)",
                  boxShadow: i === index ? "0 0 14px rgba(var(--glow),0.6)" : "none",
                }}
              />
            ))}
          </div>

          <button
            onClick={next}
            aria-label="Next testimonial"
            data-testid="testimonial-next"
            className="grid place-items-center h-11 w-11 rounded-full border border-white/15 text-fg/70 hover:text-primary-c hover:border-primary-c/60 transition-colors"
          >
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
